/**
 * After a signed-in customer loses global auth (logout / idle / revoke / consent),
 * in-progress QR payment UI must not keep rendering as a guest surface.
 * Pure guests (never had a token) stay on checkout payment by design.
 *
 * In-memory `hadGlobalAuth` alone is lost on remount/reload — combine with
 * `paymentSurfaceAuthSeen` durable sessionStorage for remount-safe leave.
 */

import {
  markPaymentSurfaceAuthSeen,
  readPaymentSurfaceAuthSeen,
} from '../../app/pwa/paymentSurfaceAuthSeen.js';
import { resolveCustomerSignInPath } from '../auth/customerAuthPaths.js';

export type LeavePaymentOnAuthLossDecision = 'mark-authed' | 'leave' | 'noop';

export function resolveLeavePaymentOnAuthLoss(input: {
  readonly sessionHydrated: boolean;
  readonly hadGlobalAuth: boolean;
  readonly globallySignedIn: boolean;
}): LeavePaymentOnAuthLossDecision {
  if (!input.sessionHydrated) {
    return 'noop';
  }
  if (input.globallySignedIn) {
    return 'mark-authed';
  }
  if (input.hadGlobalAuth) {
    return 'leave';
  }
  return 'noop';
}

/**
 * Same decision as `resolveLeavePaymentOnAuthLoss`, but ORs in-memory seen
 * with durable sessionStorage (`readPaymentSurfaceAuthSeen`).
 * On `mark-authed`, optionally persists durable seen when tenant + surface are set.
 */
export function resolveLeavePaymentOnAuthLossDurable(input: {
  readonly sessionHydrated: boolean;
  readonly globallySignedIn: boolean;
  readonly tenantCode: string;
  readonly surfaceKey: string;
  /** Current-mount ref; ORed with durable storage. */
  readonly hadGlobalAuthInMemory?: boolean;
  /** Persist durable mark when decision is mark-authed (default true). */
  readonly persistMark?: boolean;
}): LeavePaymentOnAuthLossDecision {
  const durableSeen = readPaymentSurfaceAuthSeen(input.tenantCode, input.surfaceKey);
  const hadGlobalAuth = Boolean(input.hadGlobalAuthInMemory) || durableSeen;
  const decision = resolveLeavePaymentOnAuthLoss({
    sessionHydrated: input.sessionHydrated,
    hadGlobalAuth,
    globallySignedIn: input.globallySignedIn,
  });
  if (decision === 'mark-authed' && input.persistMark !== false) {
    markPaymentSurfaceAuthSeen(input.tenantCode, input.surfaceKey);
  }
  return decision;
}

/** Path-tenant sign-in SSOT — platform `/sign-in` would drop per-tenant parks (G4). */
export function buildSignInPathAfterPaymentAuthLoss(tenantCode: string): string {
  return resolveCustomerSignInPath(tenantCode);
}
